import { useRef } from 'react';

import { useFeatureAnnouncements } from './_hooks';
import FeatureAnnouncementCard from './components/viewFeatureAnnouncements/FeatureAnnouncementCard';
import FeatureAnnouncementSpotlight from './components/viewFeatureAnnouncements/FeatureAnnouncementSpotlight';

import { Popover, PopoverAnchor, PopoverArrow, PopoverContent } from '@/app/ui/popover';

// Mounted once, inside the authenticated shell, and renders at most one release tooltip
// anchored to whichever announced element the current page happens to show.
//
// Anchored with `virtualRef` because the target is an ordinary element somewhere else in the
// tree — a tab, a button — that knows nothing about this. Radix measures whatever the ref
// holds and repositions on scroll and resize, so announcing a feature stays a one-attribute
// change at the element rather than wrapping it in a trigger.
const ViewFeatureAnnouncements = () => {
  const { announcement, element, remainingCount, dismiss, close } = useFeatureAnnouncements();
  const anchorRef = useRef(null);

  anchorRef.current = element;

  if (!announcement) {
    return null;
  }

  // Clicking outside does not close this, and that is the point: the spotlight below swallows
  // every click on the page behind it, so `Got It` and the close button are the only two ways
  // forward. An outside click that dismissed as well would make the dim a third exit nobody
  // asked for, and one that is easy to hit by accident.
  //
  // Escape still closes it, the same as the close button does.
  const preventDefault = (event) => event.preventDefault();

  const handleOpenChange = (isOpen) => {
    if (!isOpen) {
      close();
    }
  };

  // `modal={false}` even though this now behaves as a modal: the barrier is the spotlight's
  // own overlay, not Radix's modal mode, which blocks the page by writing
  // `pointer-events: none` onto `document.body`. See FeatureAnnouncementSpotlight for why that
  // trade is worth making on a component mounted under every authenticated page. The cost is
  // that focus is not trapped, so the page behind is still reachable by Tab.
  return (
    <Popover key={announcement.id} open modal={false} onOpenChange={handleOpenChange}>
      <PopoverAnchor virtualRef={anchorRef} />
      <FeatureAnnouncementSpotlight element={element} />
      <PopoverContent
        side="bottom"
        align="start"
        // Clears the spotlight ring drawn 6px outside the element, so the arrow starts where
        // the highlight ends rather than on top of it.
        sideOffset={16}
        collisionPadding={12}
        // Above the spotlight's own z-50, which is what puts the dim over the nav rail and
        // keeps the card the one thing on the page still taking a click. The background is
        // the plain popover token rather than a tint, because the arrow below has to be the
        // same colour as this to read as part of it.
        //
        // w-96 rather than the popover default: at 320px the summary broke into six-word
        // lines, which is what made a four-line paragraph read as a wall.
        className="z-[60] w-96 border-primary/40 shadow-xl"
        onInteractOutside={preventDefault}
        // FEATURE_ANNOUNCEMENT_LAYER_ATTRIBUTE, so a sheet the tooltip points into does not
        // close when the card is pressed. The spotlight carries it too.
        data-feature-announcement-layer=""
        data-testid="feature-announcement-popover"
      >
        {/*
          A rotated square straddling the top edge rather than a filled triangle sitting
          below it: the card's own fill hides the border line across the arrow's base, and
          the square's two outward sides carry the same border the card does, so the arrow
          reads as a corner of the popover instead of a separate shape stuck to it.

          `border-b border-r` is the outward pair on every side Radix can place this. The
          arrow's frame is rotated by a multiple of 90° per side, and that maps the
          bottom-right corner onto whichever corner ends up facing away from the card.
        */}
        <PopoverArrow className="size-3 translate-y-[calc(-50%_-_1px)] rotate-45 rounded-[2px] border-r border-b border-primary/40 bg-popover fill-popover" />
        <FeatureAnnouncementCard announcement={announcement} remainingCount={remainingCount} onDismiss={dismiss} onClose={close} />
      </PopoverContent>
    </Popover>
  );
};

export default ViewFeatureAnnouncements;
